<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { useMapGetter } from 'dashboard/composables/store';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  audience: {
    type: Array,
    default: () => [],
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  error: {
    type: String,
    default: '',
  },
  sent: {
    type: Number,
    default: 0,
  },
  failed: {
    type: Number,
    default: 0,
  },
  processed: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_ERROR = 'error';
const STATUS_RUNNING = 'running';

const { formatMessage } = useMessageFormatter();

// Labels from store to map audience ids to full label objects
const accountLabels = useMapGetter('labels/getLabels');
const MAX_AUDIENCE_LABELS = 5;

const audienceLabels = computed(() => {
  const ids = new Set(
    (props.audience || [])
      .filter(
        item =>
          item &&
          item.type === 'Label' &&
          item.id !== null &&
          item.id !== undefined
      )
      .map(item => item.id)
  );
  return (accountLabels.value || []).filter(label => ids.has(label.id));
});

const visibleAudienceLabels = computed(() =>
  audienceLabels.value.slice(0, MAX_AUDIENCE_LABELS)
);
const hiddenAudienceCount = computed(() => {
  return Math.max(0, audienceLabels.value.length - MAX_AUDIENCE_LABELS);
});
const hiddenAudienceLabels = computed(() =>
  hiddenAudienceCount.value
    ? audienceLabels.value.slice(MAX_AUDIENCE_LABELS)
    : []
);

const isActive = computed(() =>
  props.isLiveChatType
    ? props.isEnabled
    : ![STATUS_COMPLETED, STATUS_ERROR].includes(props.status)
);

const statusTextColor = computed(() => ({
  'text-n-ruby-11': props.status === STATUS_ERROR,
  'text-n-teal-11': props.status !== STATUS_ERROR && isActive.value,
  'text-n-slate-12': props.status !== STATUS_ERROR && !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }
  if (props.status === STATUS_ERROR) {
    return t('CAMPAIGN.SMS.CARD.STATUS.ERROR');
  }
  if (props.status === STATUS_RUNNING) {
    return t('CAMPAIGN.SMS.CARD.STATUS.RUNNING');
  }
  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});

const processedCount = computed(
  () => props.processed ?? props.sent + props.failed
);

// Show metrics on desktop first row when campaign has progress or final state
const showMetrics = computed(() =>
  [STATUS_COMPLETED, STATUS_ERROR, STATUS_RUNNING].includes(props.status)
);

// Controls visibility of hidden audience labels popover on touch/small screens
const isHiddenPopoverOpen = ref(false);
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div
        class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-x-4 gap-y-1 w-full"
      >
        <!-- Left: title + status badge -->
        <div class="flex items-center gap-3 min-w-0">
          <span
            class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
          >
            {{ title }}
          </span>
          <span
            v-tooltip="status === STATUS_ERROR ? error : ''"
            class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
            :class="statusTextColor"
          >
            {{ campaignStatus }}
          </span>
        </div>
        <!-- Right: metrics (desktop shows on the right) -->
        <div
          v-if="showMetrics"
          class="flex items-center gap-2 sm:gap-4 text-xs flex-wrap sm:ml-auto sm:justify-end"
        >
          <span class="text-n-teal-11">
            {{ t('CAMPAIGN.MESSAGE_COUNT.SENT') }}: {{ sent }}
          </span>
          <span class="text-n-ruby-11">
            {{ t('CAMPAIGN.MESSAGE_COUNT.FAILED') }}: {{ failed }}
          </span>
          <span class="text-n-slate-12">
            {{ t('CAMPAIGN.MESSAGE_COUNT.PROCESSED') }}: {{ processedCount }}
          </span>
        </div>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6 w-full"
      />

      <div
        class="flex flex-wrap sm:flex-nowrap items-center w-full gap-2 sm:h-6"
      >
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
      <!-- Audience Labels -->
      <div
        v-if="visibleAudienceLabels.length"
        class="flex items-center gap-2 min-w-0 w-full flex-wrap sm:flex-nowrap"
      >
        <div
          v-for="(label, index) in visibleAudienceLabels"
          :key="label.id"
          class="flex items-center gap-1.5 min-w-0"
        >
          <div
            class="size-1.5 rounded-sm flex-shrink-0 ring-1 ring-n-alpha-1 ring-inset"
            :style="{ backgroundColor: label.color }"
          />
          <span
            class="text-xs text-n-slate-10 whitespace-nowrap"
            :class="{
              truncate:
                index === visibleAudienceLabels.length - 1 &&
                !hiddenAudienceCount,
            }"
          >
            {{ label.title }}
          </span>
        </div>
        <div
          v-if="hiddenAudienceCount"
          v-on-click-outside="() => (isHiddenPopoverOpen = false)"
          class="relative group"
        >
          <span
            class="text-xs font-medium text-n-slate-10 px-1 py-0.5 rounded border border-n-strong cursor-default"
            role="button"
            tabindex="0"
            aria-haspopup="true"
            :aria-expanded="isHiddenPopoverOpen ? 'true' : 'false'"
            :aria-label="
              t('CAMPAIGN.SHOW_MORE_LABELS', { count: hiddenAudienceCount })
            "
            @click.stop="isHiddenPopoverOpen = !isHiddenPopoverOpen"
            @keydown.enter.prevent.stop="
              isHiddenPopoverOpen = !isHiddenPopoverOpen
            "
            @keydown.space.prevent.stop="
              isHiddenPopoverOpen = !isHiddenPopoverOpen
            "
          >
            {{ `+${hiddenAudienceCount}` }}
          </span>
          <div
            class="absolute ltr:left-0 rtl:right-0 mt-1 z-10 bg-n-background border border-n-weak rounded-md shadow-lg p-2 min-w-[8rem] max-h-64 overflow-auto opacity-0 invisible pointer-events-none group-hover:opacity-100 group-hover:visible group-hover:pointer-events-auto transition-opacity duration-150"
            :class="{
              'opacity-100 visible pointer-events-auto': isHiddenPopoverOpen,
            }"
          >
            <div
              v-for="label in hiddenAudienceLabels"
              :key="label.id"
              class="flex items-center gap-1.5 py-0.5"
            >
              <div
                class="size-1.5 rounded-sm flex-shrink-0 ring-1 ring-n-alpha-1 ring-inset"
                :style="{ backgroundColor: label.color }"
              />
              <span class="text-xs text-n-slate-10 whitespace-nowrap">
                {{ label.title }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center justify-end w-full sm:w-20 gap-2">
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
